import {Injectable} from '@angular/core';
import {
  collection,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  writeBatch,
} from '@angular/fire/firestore';
import {from, forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {IStore} from '../../../shared/shared-models/store/store';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {
  PATH_OPERATIONAL_STORE_DATA_QUOTE_BY_ID,
  PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEM_BY_CODE,
  PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEMS_GET,
  PATH_OPERATIONAL_STORE_DATA_QUOTES_GET,
} from '../../../shared/shared-services/database-paths.operational';
import {path_operational_stores_data_storeId_messages_from_app} from '../../../shared/shared-services/database-paths';
import {IFirebaseQuery} from '../../../shared/shared-models/firebase/firebase-queries';
import {firebaseTimeStampToDate, generateFirebaseCollectionReference} from '../../../shared/shared-utils/firestore/firestore.utils';
import {createOperationalServerTimestamp} from '../../../shared/shared-utils/firestore/operational-server-timestamp.utils';
import {CollectionSharedStockService} from '../../../shared/shared-services/firebase/collection-shared-stock.service';
import {StockItem, sItemKeyToInt} from '../../../shared-utilities/models-old/datastructures';
import {IDebtorDetails} from '../models/debtors';
import {resolveQuoteProcessedDate} from '../utils/quote-list-date.utils';
import {
  IQuoteFirestoreDocument,
  IQuoteFirestoreItem,
  IQuoteSubmitResult,
} from '../models/quote-firestore.interface';
import {IQuoteComposeEntry} from '../models/quote-compose-entry.interface';
import {IQuoteListItem} from '../models/quote-list-item.interface';
import {IQuoteStockItem} from '../models/quoteStockItems';
import {
  mapComposeEntriesToFirestorePayload,
  mapFirestoreQuoteToComposeEntries,
} from '../utils/quote-compose-entry.utils';

export const QUOTES_LIST_LOOKBACK_DAYS = 30;

export const QUOTES_MOBILE_CARD_LIMIT = 10;

const FIRESTORE_IN_QUERY_LIMIT = 30;

const QUOTE_ITEMS_BATCH_SIZE = 400;

type IDebtorRecord = IDebtorDetails & {
  debtorId?: string;
  isDebtorSelected?: boolean;
  account?: string;
};

/**
 * Firebase access for the quotes feature — list, submit, debtors, and lazy-loaded items.
 */
@Injectable({
  providedIn: 'root',
})
export class QuotesFirebaseService {

  constructor(
    private readonly firestore: Firestore,
    private readonly collectionSharedStockService: CollectionSharedStockService,
  ) {}

  /**
   * Subscribes to quote metadata for the selected store from the last 30 days.
   */
  subscribeQuotesForStore(store: IStore): Observable<IQuoteListItem[]> {
    if (!store?.storeId) {
      return of([]);
    }

    const lookbackDate = this.buildLookbackDate();
    const collectionRef = collection(this.firestore, PATH_OPERATIONAL_STORE_DATA_QUOTES_GET(store.storeId));
    const firebaseQueries: IFirebaseQuery[] = [
      {q: 'where', p: ['creationDate', '>=', lookbackDate]},
    ];
    const queryRef = generateFirebaseCollectionReference(firebaseQueries, collectionRef);

    return new Observable<IQuoteListItem[]>((subscriber) => {
      const unsubscribe = onSnapshot(
        queryRef,
        (snapshot) => {
          const quotes: IQuoteListItem[] = [];
          snapshot.forEach((docSnapshot: QueryDocumentSnapshot) => {
            const data = docSnapshot.data() as IQuoteFirestoreDocument;
            quotes.push(this.mapQuoteDocument(docSnapshot.id, data));
          });
          quotes.sort((a: IQuoteListItem, b: IQuoteListItem) => {
            const aDate = a.processedDate ?? a.creationDate;
            const bDate = b.processedDate ?? b.creationDate;
            return (bDate?.getTime() ?? 0) - (aDate?.getTime() ?? 0);
          });
          subscriber.next(quotes);
        },
        (error: Error) => {
          subscriber.error(error);
        },
      );

      return (): void => {
        unsubscribe();
      };
    });
  }

  /**
   * @deprecated Use subscribeQuotesForStore for live updates.
   */
  getQuotesForStore(store: IStore): Observable<IQuoteListItem[]> {
    if (!store?.storeId) {
      return of([]);
    }

    const lookbackDate = this.buildLookbackDate();
    const collectionRef = collection(this.firestore, PATH_OPERATIONAL_STORE_DATA_QUOTES_GET(store.storeId));
    const firebaseQueries: IFirebaseQuery[] = [
      {q: 'where', p: ['creationDate', '>=', lookbackDate]},
    ];
    const queryRef = generateFirebaseCollectionReference(firebaseQueries, collectionRef);

    return new Observable<IQuoteListItem[]>((subscriber) => {
      void getDocs(queryRef)
        .then((snapshot) => {
          const quotes: IQuoteListItem[] = [];
          snapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data() as IQuoteFirestoreDocument;
            quotes.push(this.mapQuoteDocument(docSnapshot.id, data));
          });
          quotes.sort((a: IQuoteListItem, b: IQuoteListItem) => {
            const aDate = a.processedDate ?? a.creationDate;
            const bDate = b.processedDate ?? b.creationDate;
            return (bDate?.getTime() ?? 0) - (aDate?.getTime() ?? 0);
          });
          subscriber.next(quotes);
          subscriber.complete();
        })
        .catch((error: Error) => {
          subscriber.error(error);
        });
    }).pipe(catchError(() => of([])));
  }

  /**
   * Loads a single quote document (metadata only).
   */
  getQuoteDocument(storeId: string, quoteId: string): Observable<IQuoteFirestoreDocument & {id: string}> {
    const quoteRef = doc(this.firestore, PATH_OPERATIONAL_STORE_DATA_QUOTE_BY_ID(storeId, quoteId));
    return from(getDoc(quoteRef)).pipe(
      map((snapshot) => {
        if (!snapshot.exists()) {
          throw new Error('Quote not found');
        }
        return {
          id: snapshot.id,
          ...(snapshot.data() as IQuoteFirestoreDocument),
        };
      }),
    );
  }

  /**
   * Loads quote compose rows when a quote is opened, preserving sections and order.
   */
  getQuoteComposeEntriesForOpen(
    store: IStore,
    quoteId: string,
  ): Observable<IQuoteComposeEntry[]> {
    if (!store?.storeId) {
      return of([]);
    }

    return forkJoin({
      quote: this.getQuoteDocument(store.storeId, quoteId),
      savedItems: from(this.loadQuoteItemDocuments(store.storeId, quoteId)).pipe(
        catchError(() => of([] as IQuoteFirestoreItem[])),
      ),
    }).pipe(
      switchMap(({quote, savedItems}) => {
        if (savedItems.length === 0) {
          return of(mapFirestoreQuoteToComposeEntries(quote.quoteSections, [], []));
        }

        const codes: string[] = savedItems.map((item: IQuoteFirestoreItem) => item.code);
        return this.fetchStockItemsByCodes(store, codes).pipe(
          map((stockItems: StockItem[]) => {
            const mergedItems = this.mergeQuoteItemsWithStock(savedItems, stockItems);
            return mapFirestoreQuoteToComposeEntries(quote.quoteSections, savedItems, mergedItems);
          }),
          catchError(() => of(mapFirestoreQuoteToComposeEntries(quote.quoteSections, savedItems, this.mergeQuoteItemsWithStock(savedItems, [])))),
        );
      }),
      catchError(() => of([] as IQuoteComposeEntry[])),
    );
  }

  /**
   * Loads quote line items when a quote is opened, enriching with current stock where available.
   */
  getQuoteItemsForOpen(store: IStore, quoteId: string): Observable<IQuoteStockItem[]> {
    if (!store?.storeId) {
      return of([]);
    }

    return from(this.loadQuoteItemDocuments(store.storeId, quoteId)).pipe(
      catchError(() => of([] as IQuoteFirestoreItem[])),
      switchMap((savedItems: IQuoteFirestoreItem[]) => {
        if (savedItems.length === 0) {
          return of([]);
        }

        const codes: string[] = savedItems.map((item: IQuoteFirestoreItem) => item.code);
        return this.fetchStockItemsByCodes(store, codes).pipe(
          map((stockItems: StockItem[]) => this.mergeQuoteItemsWithStock(savedItems, stockItems)),
          catchError(() => of(this.mergeQuoteItemsWithStock(savedItems, []))),
        );
      }),
      catchError(() => of([] as IQuoteStockItem[])),
    );
  }

  /**
   * Persists a submitted quote, its quote_items subcollection, and the from_app message.
   */
  async submitQuote(
    storeId: string,
    userId: string,
    debtorData: IDebtorDetails,
    comment: string | null,
    composeEntries: IQuoteComposeEntry[],
  ): Promise<IQuoteSubmitResult> {
    const {sections, items: firestoreItems} = mapComposeEntriesToFirestorePayload(composeEntries);

    const quoteCollectionPath = PATH_OPERATIONAL_STORE_DATA_QUOTES_GET(storeId);
    const quoteRef = doc(collection(this.firestore, quoteCollectionPath));
    const quoteId = quoteRef.id;
    const operationalTimestamp = createOperationalServerTimestamp();

    const messageCollectionPath = path_operational_stores_data_storeId_messages_from_app(storeId);

    const quoteDocument: IQuoteFirestoreDocument = {
      comment: comment ?? '',
      debtorData,
      itemCount: firestoreItems.length,
      quoteId,
      quoteSections: sections.length > 0 ? sections : undefined,
      status: 'SUBMITTED',
      storeId,
      type: 'QUOTES',
      userId,
    };

    const firstBatch = writeBatch(this.firestore);
    firstBatch.set(quoteRef, {
      ...this.sanitizeForFirestore(quoteDocument),
      creationDate: operationalTimestamp,
    });

    let batch = firstBatch;
    let batchCount = 1;

    for (const item of firestoreItems) {
      const itemRef = doc(
        this.firestore,
        PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEM_BY_CODE(storeId, quoteId, item.code),
      );
      batch.set(itemRef, this.sanitizeForFirestore(item));
      batchCount += 1;

      if (batchCount >= QUOTE_ITEMS_BATCH_SIZE) {
        await batch.commit();
        batch = writeBatch(this.firestore);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    const messageRef = doc(collection(this.firestore, messageCollectionPath));

    const messageBatch = writeBatch(this.firestore);
    messageBatch.set(messageRef, {
      payload: {
        data: {
          logID: quoteId,
        },
      },
      sender: userId,
      timestamp: createOperationalServerTimestamp(),
      type: 'QUOTES',
    });
    await messageBatch.commit();

    return {
      quoteId,
      messageId: messageRef.id,
    };
  }

  /**
   * Loads debtors and merges contact details from debtors_contacts.
   */
  getDebtorsWithContactsForStore(store: IStore): Observable<Record<string, IDebtorRecord>> {
    if (!store?.storeId) {
      return of({});
    }

    const sharedPaths = createSharedPaths().setStoreId(store.storeId);
    const debtorsPath = sharedPaths.debtors();
    const contactsPath = sharedPaths.debtorsContacts();
    const debtorsRef = collection(this.firestore, debtorsPath);
    const contactsRef = collection(this.firestore, contactsPath);

    // Temporary: log Firestore paths so security rules can be updated for quote debtor load.
    console.info('[Quotes] Loading debtors from Firestore paths:', {
      debtors: debtorsPath,
      debtorsContacts: contactsPath,
      storeId: store.storeId,
    });

    return from(getDocs(debtorsRef)).pipe(
      switchMap((debtorsSnapshot) => {
        const debtors = this.mapDebtorSnapshots(debtorsSnapshot.docs);
        return from(getDocs(contactsRef)).pipe(
          map((contactsSnapshot) => this.mergeDebtorsWithContacts(
            debtors,
            contactsSnapshot.docs.map((contactDoc) => ({
              ...contactDoc.data(),
              _firestoreDocId: contactDoc.id,
            })),
          )),
          catchError((contactsError: Error) => {
            console.warn('[Quotes] Failed to load debtors_contacts (falling back to debtors only):', {
              path: contactsPath,
              error: contactsError.message,
            });
            return of(debtors);
          }),
        );
      }),
      catchError((debtorsError: Error) => {
        console.error('[Quotes] Failed to load debtors — check Firestore rules for this path:', {
          path: debtorsPath,
          error: debtorsError.message,
        });
        throw debtorsError;
      }),
    );
  }

  private async loadQuoteItemDocuments(storeId: string, quoteId: string): Promise<IQuoteFirestoreItem[]> {
    const itemsRef = collection(this.firestore, PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEMS_GET(storeId, quoteId));
    const itemsSnapshot = await getDocs(itemsRef);

    if (!itemsSnapshot.empty) {
      return itemsSnapshot.docs.map((itemDoc) => ({
        ...(itemDoc.data() as IQuoteFirestoreItem),
        code: itemDoc.id,
      }));
    }

    const quoteRef = doc(this.firestore, PATH_OPERATIONAL_STORE_DATA_QUOTE_BY_ID(storeId, quoteId));
    const quoteSnapshot = await getDoc(quoteRef);
    if (!quoteSnapshot.exists()) {
      return [];
    }

    const quoteData = quoteSnapshot.data() as IQuoteFirestoreDocument;
    return this.mapLegacyQuoteItems(quoteData.quoteItems);
  }

  private mapLegacyQuoteItems(
    quoteItems: IQuoteFirestoreDocument['quoteItems'],
  ): IQuoteFirestoreItem[] {
    if (!quoteItems) {
      return [];
    }

    if (Array.isArray(quoteItems)) {
      return quoteItems
        .filter((item: IQuoteFirestoreItem) => !!item?.code)
        .map((item: IQuoteFirestoreItem) => ({
          code: item.code,
          desc: item.desc,
          itemDiscount: Number(item.itemDiscount ?? 0),
          nominalGP: item.nominalGP,
          orderQty: Number(item.orderQty ?? 0),
          originalSellPriIncl1: item.originalSellPriIncl1,
          sellPriIncl1: Number(item.sellPriIncl1 ?? 0),
        }));
    }

    return Object.entries(quoteItems).map(([code, item]) => ({
      code,
      desc: item.desc,
      itemDiscount: Number(item.itemDiscount ?? 0),
      nominalGP: item.nominalGP,
      orderQty: Number(item.orderQty ?? 0),
      originalSellPriIncl1: item.originalSellPriIncl1,
      sellPriIncl1: Number(item.sellPriIncl1 ?? 0),
    }));
  }

  private fetchStockItemsByCodes(store: IStore, codes: string[]): Observable<StockItem[]> {
    if (codes.length === 0) {
      return of([]);
    }

    const chunks: string[][] = [];
    for (let index = 0; index < codes.length; index += FIRESTORE_IN_QUERY_LIMIT) {
      chunks.push(codes.slice(index, index + FIRESTORE_IN_QUERY_LIMIT));
    }

    const requests: Observable<StockItem[]>[] = chunks.map((chunk: string[]) => {
      const query: IFirebaseQuery[] = [
        {q: 'where', p: [sItemKeyToInt.code, 'in', chunk]},
      ];
      return this.collectionSharedStockService.getStockItemsCast(store, query, 'objectID').pipe(
        take(1),
      );
    });

    return forkJoin(requests).pipe(
      map((results: StockItem[][]) => results.flat()),
      catchError(() => of([])),
    );
  }

  private mergeQuoteItemsWithStock(
    savedItems: IQuoteFirestoreItem[],
    stockItems: StockItem[],
  ): IQuoteStockItem[] {
    const stockByCode = new Map<string, StockItem>(
      stockItems.map((item: StockItem) => [item.code, item]),
    );

    return savedItems.map((savedItem: IQuoteFirestoreItem): IQuoteStockItem => {
      const stockItem = stockByCode.get(savedItem.code);
      const baseItem: IQuoteStockItem = stockItem
        ? {
          ...stockItem,
          nominalGP: stockItem.nominalGP ?? savedItem.nominalGP ?? 0,
          total: 0,
        }
        : {
          code: savedItem.code,
          desc: savedItem.desc ?? savedItem.code,
          nominalGP: savedItem.nominalGP ?? 0,
          total: 0,
        } as IQuoteStockItem;

      return {
        ...baseItem,
        code: savedItem.code,
        desc: savedItem.desc ?? baseItem.desc,
        itemDiscount: savedItem.itemDiscount,
        nominalGP: savedItem.nominalGP ?? baseItem.nominalGP ?? 0,
        orderQty: savedItem.orderQty,
        originalSellPriIncl1: savedItem.originalSellPriIncl1 ?? savedItem.sellPriIncl1,
        sellPriIncl1: savedItem.sellPriIncl1,
        total: 0,
      };
    });
  }

  private mapQuoteItemForFirestore(
    item: IQuoteFirestoreItem & { sortOrder?: number },
  ): IQuoteFirestoreItem {
    const nominalGP = Number(item.nominalGP);
    const firestoreItem: IQuoteFirestoreItem = {
      code: item.code,
      desc: item.desc ?? item.code,
      itemDiscount: Number(item.itemDiscount ?? 0),
      orderQty: Number(item.orderQty ?? 0),
      originalSellPriIncl1: Number(item.originalSellPriIncl1 ?? item.sellPriIncl1 ?? 0),
      sellPriIncl1: Number(item.sellPriIncl1 ?? 0),
      sectionId: item.sectionId,
      sortOrder: item.sortOrder,
    };

    if (Number.isFinite(nominalGP)) {
      firestoreItem.nominalGP = nominalGP;
    }

    return firestoreItem;
  }

  /**
   * Removes undefined and non-finite numbers so Firestore accepts the payload.
   */
  private sanitizeForFirestore<T>(value: T): T {
    if (value === undefined) {
      return value;
    }

    if (value === null) {
      return value;
    }

    if (typeof value === 'number') {
      return (Number.isFinite(value) ? value : null) as T;
    }

    if (value instanceof Date || value instanceof Timestamp) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((entry: unknown) => this.sanitizeForFirestore(entry)) as T;
    }

    if (typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .filter(([, entry]) => entry !== undefined)
          .map(([key, entry]) => [key, this.sanitizeForFirestore(entry)]),
      ) as T;
    }

    return value;
  }

  private buildLookbackDate(): Date {
    const lookbackDate = new Date();
    lookbackDate.setDate(lookbackDate.getDate() - QUOTES_LIST_LOOKBACK_DAYS);
    lookbackDate.setHours(0, 0, 0, 0);
    return lookbackDate;
  }

  private mapQuoteDocument(id: string, data: IQuoteFirestoreDocument): IQuoteListItem {
    const creationDate = this.toDate(data.creationDate);
    const processedDate = resolveQuoteProcessedDate(data);
    const debtorData = data.debtorData;
    const debtorId = debtorData?.debtorId ?? debtorData?.account ?? '';
    const debtorName = debtorData?.name ?? (debtorId || 'Unknown debtor');

    return {
      id,
      creationDate,
      debtorId,
      debtorName,
      documentNo: data.purchaseOrderNo ?? data.quoteNo ?? data.documentNo ?? '',
      emailStatus: data.emailStatus ?? '',
      itemCount: this.resolveItemCount(data),
      iqStatus: data.iqStatus ?? '',
      pdfStatus: data.pdfStatus ?? '',
      processedDate,
      status: data.status ?? 'UNKNOWN',
      storeId: data.storeId ?? '',
      userId: data.userId ?? '',
    };
  }

  private resolveItemCount(data: IQuoteFirestoreDocument): number {
    if (typeof data.itemCount === 'number') {
      return data.itemCount;
    }

    if (!data.quoteItems) {
      return 0;
    }

    if (Array.isArray(data.quoteItems)) {
      return data.quoteItems.length;
    }

    return Object.keys(data.quoteItems).length;
  }

  private toDate(value: IQuoteFirestoreDocument['creationDate']): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    return firebaseTimeStampToDate(value as Timestamp);
  }

  private mapDebtorSnapshots(docs: QueryDocumentSnapshot[]): Record<string, IDebtorRecord> {
    const debtors: Record<string, IDebtorRecord> = {};
    docs.forEach((docSnapshot: QueryDocumentSnapshot): void => {
      const debtorData = docSnapshot.data();
      const debtorId = this.resolveDebtorId(debtorData, docSnapshot.id);
      if (!debtorId) {
        return;
      }
      debtors[debtorId] = {
        ...(debtorData as IDebtorRecord),
        debtorId,
        account: (debtorData['account'] as string | undefined)?.trim() || debtorId,
        isDebtorSelected: false,
      };
    });
    return debtors;
  }

  private resolveDebtorId(data: DocumentData, documentId: string): string {
    const account = data['account'] as string | undefined;
    const debtorId = data['debtorId'] as string | undefined;
    const id = data['id'] as string | undefined;
    if (account?.trim()) {
      return account.trim();
    }
    if (debtorId?.trim()) {
      return debtorId.trim();
    }
    if (id?.trim()) {
      return id.trim();
    }
    if (documentId?.trim()) {
      return documentId.trim();
    }
    return '';
  }

  private mergeDebtorsWithContacts(
    debtors: Record<string, IDebtorRecord>,
    contacts: DocumentData[],
  ): Record<string, IDebtorRecord> {
    const merged: Record<string, IDebtorRecord> = {...debtors};

    contacts.forEach((contactData: DocumentData): void => {
      const documentId = (contactData['_firestoreDocId'] as string | undefined) ?? '';
      const debtorId = this.resolveDebtorId(contactData, documentId);
      if (!debtorId) {
        return;
      }

      const existing = merged[debtorId] ?? (contactData as IDebtorRecord);
      merged[debtorId] = {
        ...existing,
        ...contactData,
        debtorId,
        account: existing.account ?? (contactData['account'] as string | undefined) ?? debtorId,
        name: existing.name ?? (contactData['name'] as string | undefined) ?? '',
        email: (contactData['email'] as string | undefined) ?? existing.email,
        telephone1: (contactData['telephone1'] as string | undefined)
          ?? (contactData['telephone'] as string | undefined)
          ?? existing.telephone1,
        contact: (contactData['contact'] as string | undefined) ?? existing.contact,
        isDebtorSelected: false,
      };
    });

    return merged;
  }
}
